import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { ParticipantListDto, RaterDetailDto, RaterRelationship } from '@genoma/shared';

import { Centered } from '../../components/Centered.tsx';
import { useI18n } from '../../i18n/index.tsx';
import {
  addRaterAssignment,
  fetchParticipants,
  fetchRater,
  removeRaterAssignment,
} from '../../services/dashboard.service.ts';
import { useCan } from '../../stores/auth.store.ts';
import { InvitationCard } from '../participants/InvitationCard.tsx';

/**
 * One rater: who they were asked about, and how far they have got with each.
 *
 * The same shape as an evaluated person's card, minus the report — there is
 * nothing to report about somebody who was only ever asked to assess others.
 *
 * Progress is per person rather than per questionnaire because that is where
 * it stalls: somebody assessing nine people typically answers about the three
 * they know well and leaves the rest, and a single "not submitted" hides that
 * completely.
 */
/**
 * Asking before taking somebody off the list.
 *
 * A window rather than the two-step buttons used elsewhere on this screen:
 * those turn red and wait, which is enough for "resend an invitation" but not
 * for an edit to the 360 map. This one has to say who is being removed and
 * what it costs, and it has room to.
 *
 * Not `window.confirm`, which cannot say either of those things and looks like
 * the browser talking rather than the product.
 */
const ConfirmRemoval = ({
  name,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  name: string;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useI18n();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('raters.removeTitle')}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-5"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-[440px] rounded-2xl border border-line bg-paper p-5 shadow-card"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-[17px] font-bold tracking-[-0.02em]">{t('raters.removeTitle')}</h2>
        <p className="mt-2 text-[14px] leading-relaxed text-soft">
          {t('raters.removeBody', { name })}
        </p>
        <p className="mt-2 text-[13px] leading-relaxed text-faint">{t('raters.removeNote')}</p>

        {error && <p className="mt-3 text-[13px] text-[var(--color-critical)]">{error}</p>}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="cursor-pointer border-none bg-transparent px-2 py-2 font-mono text-[11px] tracking-[0.06em] text-soft uppercase"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="cursor-pointer rounded-xl bg-[var(--color-critical)] px-4 py-2.5 text-[13.5px] font-bold text-white transition disabled:opacity-40"
          >
            {busy ? t('common.saving') : t('raters.removeConfirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

/** Relationships somebody can be added under. A self-assessment is not one. */
const ADDABLE: Exclude<RaterRelationship, 'SELF'>[] = [
  'PEER',
  'DIRECT_REPORT',
  'STAKEHOLDER',
  'SUPERVISOR',
];

/**
 * Asking before putting somebody on the list — and asking *how*.
 *
 * The relationship is the second question, and it cannot be skipped: it decides
 * which segment these answers land in, and therefore whether that segment
 * reaches the three people it needs to be shown separately in a report. A
 * default here would be the product inventing an org chart.
 */
const ConfirmAddition = ({
  rater,
  name,
  relationship,
  onRelationship,
  busy,
  error,
  onCancel,
  onConfirm,
}: {
  rater: string;
  name: string;
  relationship: Exclude<RaterRelationship, 'SELF'>;
  onRelationship: (next: Exclude<RaterRelationship, 'SELF'>) => void;
  busy: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) => {
  const { t } = useI18n();

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={t('raters.addTitle')}
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-5"
      onClick={onCancel}
    >
      <div
        className="w-full max-w-[460px] rounded-2xl border border-line bg-paper p-5 shadow-card"
        onClick={(event) => event.stopPropagation()}
      >
        <h2 className="text-[17px] font-bold tracking-[-0.02em]">{t('raters.addTitle')}</h2>
        <p className="mt-2 text-[14px] leading-relaxed text-soft">
          {t('raters.addBody', { rater, name })}
        </p>

        <label className="mt-4 block">
          <span className="block font-mono text-[10px] tracking-[0.12em] text-faint uppercase">
            {t('raters.addRelationship')}
          </span>
          <select
            value={relationship}
            autoFocus
            onChange={(event) =>
              onRelationship(event.target.value as Exclude<RaterRelationship, 'SELF'>)
            }
            className="mt-1 w-full cursor-pointer rounded-xl border border-line bg-sand px-3 py-2 text-[14px] outline-none focus:border-teal"
          >
            {ADDABLE.map((option) => (
              <option key={option} value={option}>
                {t(`relationships.${option}`)}
              </option>
            ))}
          </select>
          <span className="mt-1.5 block text-[12.5px] text-faint">
            {t('raters.addRelationshipHelp')}
          </span>
        </label>

        {error && <p className="mt-3 text-[13px] text-[var(--color-critical)]">{error}</p>}

        <div className="mt-4 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="cursor-pointer border-none bg-transparent px-2 py-2 font-mono text-[11px] tracking-[0.06em] text-soft uppercase"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={onConfirm}
            className="cursor-pointer rounded-xl bg-teal px-4 py-2.5 text-[13.5px] font-bold text-white shadow-action transition hover:bg-teal-deep disabled:opacity-40"
          >
            {busy ? t('common.saving') : t('raters.addConfirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export const RaterDetailPage = () => {
  const { t } = useI18n();
  const { personId } = useParams<{ personId: string }>();

  const [detail, setDetail] = useState<RaterDetailDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Editing the map is the consultancy's: P&O and coaches never see this. */
  const canEditMap = useCan('rater-map:edit');
  const [removing, setRemoving] = useState<{ participantId: string; name: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);

  const [everyone, setEveryone] = useState<ParticipantListDto | null>(null);
  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState<{ participantId: string; name: string } | null>(null);
  const [relationship, setRelationship] = useState<Exclude<RaterRelationship, 'SELF'>>('PEER');
  const [addError, setAddError] = useState<string | null>(null);

  const confirmAddition = async () => {
    if (!personId || !adding) return;
    setBusy(true);
    setAddError(null);
    try {
      setDetail(await addRaterAssignment(personId, adding.participantId, relationship));
      setAdding(null);
      setSearch('');
    } catch (caught) {
      setAddError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  const confirmRemoval = async () => {
    if (!personId || !removing) return;
    setBusy(true);
    setRemoveError(null);
    try {
      setDetail(await removeRaterAssignment(personId, removing.participantId));
      setRemoving(null);
    } catch (caught) {
      setRemoveError(caught instanceof Error ? caught.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (!personId) return;
    fetchRater(personId)
      .then(setDetail)
      .catch((caught: unknown) =>
        setError(caught instanceof Error ? caught.message : t('errors.generic')),
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId]);

  useEffect(() => {
    if (!canEditMap) return;
    fetchParticipants().then(setEveryone).catch(() => setEveryone(null));
  }, [canEditMap]);

  if (error) return <Centered>{error}</Centered>;
  if (!detail) return <Centered>{t('common.loading')}</Centered>;

  const { person, assesses } = detail;
  /**
   * Finished means finished: every rating in *and* the open answer written.
   *
   * Counting ratings alone was the same bug submission was fixed for — it let
   * somebody parked on the last screen, one written line short, read as done on
   * the screen P&O chase participation from.
   */
  const done = assesses.filter((entry) => entry.answered >= entry.total && entry.written).length;

  /**
   * Answered in full and still not sent.
   *
   * The other half of the same misreading: with every row complete the list
   * says nothing is outstanding, while the questionnaire sits unsubmitted and
   * absent from every report. The invitation card below reports on the
   * invitation, not on this, so without a line here the page has no way to say
   * it — and this is the one person on the screen who needs a nudge rather than
   * a chase.
   */
  const unsent =
    assesses.length > 0 &&
    done === assesses.length &&
    detail.invitations.some((invitation) => invitation.status !== 'SUBMITTED');

  // Everybody in the cycle who is not already on this list, matched loosely on
  // name and job title. Shown only once something is typed: nineteen names
  // under a search box read as the list itself.
  const alreadyOn = new Set(assesses.map((entry) => entry.participantId));
  const candidates = (() => {
    const needle = search.trim().toLowerCase();
    if (!needle || !everyone) return [];
    return everyone.participants
      .filter((candidate) => !alreadyOn.has(candidate.participantId))
      .filter((candidate) => candidate.personId !== person.id)
      // The code is searched alongside the name because it is what the
      // client's own spreadsheets say — somebody reading "MVAC" off another
      // document should be able to type that rather than spell a surname.
      .filter((candidate) =>
        [candidate.fullName, candidate.jobTitle ?? '', candidate.externalCode ?? '']
          .join(' ')
          .toLowerCase()
          .includes(needle),
      )
      .slice(0, 6);
  })();

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <Link
        to="/evaluadores"
        className="font-mono text-[10px] tracking-[0.12em] text-soft uppercase hover:text-teal"
      >
        ← {t('raters.title')}
      </Link>

      <header className="mt-3">
        <h1 className="text-[26px] font-extrabold tracking-[-0.025em]">{person.fullName}</h1>
        <p className="mt-0.5 text-[13.5px] text-soft">
          {person.jobTitle ?? t('raters.noJobTitle')}
          {person.area && ` · ${person.area}`}
        </p>
      </header>

      <section className="mt-6 rounded-2xl border border-line bg-paper px-4 py-4 shadow-card">
        <h2 className="font-mono text-[10px] tracking-[0.13em] text-faint uppercase">
          {t('raters.assessesHeading', { count: assesses.length })}
        </h2>
        <p className="mt-1 text-[13px] text-soft">
          {t('raters.assessesLead', { done, total: assesses.length })}
        </p>

        {unsent && (
          <p className="mt-1 text-[13px] font-semibold text-teal-deep">
            {t('raters.doneButUnsent')}
          </p>
        )}

        {canEditMap && (
          <div className="mt-3">
            <input
              type="search"
              value={search}
              disabled={!detail.listIsOpen}
              placeholder={t('raters.addPlaceholder')}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full rounded-xl border border-line bg-sand px-3.5 py-2.5 text-[14px] outline-none placeholder:text-faint focus:border-teal disabled:cursor-not-allowed disabled:bg-line-soft/40 disabled:placeholder:text-faint/60"
            />

            {/* Said once, under the box, rather than as a tooltip nobody
                hovers: the reason the list is fixed is the interesting part. */}
            {!detail.listIsOpen && (
              <p className="mt-1.5 text-[12.5px] text-faint">{t('raters.addBlocked')}</p>
            )}

            {candidates.length > 0 && (
              <ul className="mt-2 list-none overflow-hidden rounded-xl border border-line bg-paper shadow-card">
                {candidates.map((candidate) => (
                  <li key={candidate.participantId}>
                    <button
                      type="button"
                      onClick={() => {
                        setAddError(null);
                        setAdding({
                          participantId: candidate.participantId,
                          name: candidate.fullName,
                        });
                      }}
                      className="block w-full cursor-pointer border-none border-b border-line-soft bg-transparent px-3.5 py-2.5 text-left transition last:border-b-0 hover:bg-tint-soft"
                    >
                      <span className="flex items-baseline gap-2">
                        <span className="text-[14px] font-semibold">{candidate.fullName}</span>
                        {candidate.externalCode && (
                          <span className="font-mono text-[10.5px] tracking-[0.08em] text-faint uppercase">
                            {candidate.externalCode}
                          </span>
                        )}
                      </span>
                      <span className="block text-[12.5px] text-faint">
                        {candidate.jobTitle ?? t(`roleLevels.${candidate.roleLevel}`)}
                        {candidate.area && ` · ${candidate.area}`}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        <div className="mt-3 flex flex-col">
          {assesses.map((entry) => {
            const rated = entry.answered >= entry.total;
            const complete = rated && entry.written;
            const started = entry.answered > 0;
            /**
             * The open answer is the last step, so it is the last stretch of
             * the bar. Without it a full bar sat above a person who could not
             * submit — the exact reading this screen has to stop giving.
             */
            const progress =
              entry.total === 0
                ? 0
                : ((entry.answered + (entry.written ? 1 : 0)) / (entry.total + 1)) * 100;

            return (
              <div
                key={`${entry.participantId}-${entry.relationship}`}
                className="relative flex flex-wrap items-center justify-between gap-3 border-t border-line-soft py-2.5 transition first:border-t-0 first:pt-0 hover:bg-sand"
              >
                <span className="min-w-0">
                  <span className="block text-[14px] font-semibold">
                    {entry.participantId ? (
                      <Link
                        to={`/participantes/${entry.participantId}`}
                        className="after:absolute after:inset-0 after:content-[''] hover:text-teal"
                      >
                        {entry.fullName}
                      </Link>
                    ) : (
                      entry.fullName
                    )}
                  </span>
                  <span className="block text-[12.5px] text-faint">
                    {/* The neutral label, not the survey's second-person one:
                        this screen is somebody looking at another person's
                        list, not that person answering. */}
                    {t(`relationships.${entry.relationship}`)}
                    {entry.jobTitle && ` · ${entry.jobTitle}`}
                  </span>
                </span>

                <span className="flex shrink-0 items-center gap-2.5">
                  <span className="h-1.5 w-20 overflow-hidden rounded-full bg-line-soft">
                    <span
                      className="block h-full rounded-full bg-teal transition-[width] duration-500"
                      style={{
                        width: `${progress}%`,
                      }}
                    />
                  </span>
                  <span
                    className={[
                      'font-mono text-[10.5px] tracking-[0.06em] uppercase',
                      complete ? 'text-teal-deep' : started ? 'text-soft' : 'text-faint',
                    ].join(' ')}
                  >
                    {complete
                      ? t('raters.answeredAll')
                      : rated
                        ? t('raters.answeredNoComment')
                        : started
                          ? t('raters.answeredSome', {
                              answered: entry.answered,
                              total: entry.total,
                            })
                          : t('raters.answeredNone')}
                  </span>

                  {/* Above the row-wide link, or the row would swallow it.
                      Disabled rather than hidden once somebody has started:
                      hiding it makes the rule invisible, and the rule — that
                      an answer cannot be thrown away from here — is the thing
                      worth knowing. The title says why. */}
                  {canEditMap && entry.participantId && entry.relationship !== 'SELF' && (
                    <button
                      type="button"
                      disabled={started}
                      title={started ? t('raters.removeBlocked') : t('raters.remove')}
                      onClick={() =>
                        setRemoving({
                          participantId: entry.participantId!,
                          name: entry.fullName,
                        })
                      }
                      className="relative z-10 shrink-0 cursor-pointer rounded-xl border border-line bg-paper px-2.5 py-1 text-[12px] font-semibold text-soft transition hover:border-[var(--color-critical)] hover:text-[var(--color-critical)] disabled:cursor-not-allowed disabled:border-line-soft disabled:text-faint disabled:hover:border-line-soft disabled:hover:text-faint"
                    >
                      {t('raters.remove')}
                    </button>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </section>

      {adding && (
        <ConfirmAddition
          rater={person.fullName}
          name={adding.name}
          relationship={relationship}
          onRelationship={setRelationship}
          busy={busy}
          error={addError}
          onCancel={() => {
            setAdding(null);
            setAddError(null);
          }}
          onConfirm={() => void confirmAddition()}
        />
      )}

      {removing && (
        <ConfirmRemoval
          name={removing.name}
          busy={busy}
          error={removeError}
          onCancel={() => {
            setRemoving(null);
            setRemoveError(null);
          }}
          onConfirm={() => void confirmRemoval()}
        />
      )}

      <InvitationCard
        personId={person.id}
        detail={{
          email: person.email,
          emailIsPlaceholder: person.emailIsPlaceholder,
          locale: person.locale,
          invitations: detail.invitations,
        }}
        onChange={(changes) =>
          setDetail((current) =>
            current
              ? {
                  ...current,
                  person: {
                    ...current.person,
                    email: changes.email ?? current.person.email,
                    emailIsPlaceholder:
                      changes.emailIsPlaceholder ?? current.person.emailIsPlaceholder,
                    locale: changes.locale !== undefined ? changes.locale : current.person.locale,
                  },
                  invitations: changes.invitations ?? current.invitations,
                }
              : current,
          )
        }
      />

      <p className="mt-8 border-t border-line-soft pt-4 text-xs text-faint">
        {t('raters.privacyFooter')}
      </p>
    </div>
  );
};
